import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { fetchClient } from '../api/typedClient'
import { useTenantStore } from '../stores/tenantStore'
import { useAuth } from './AuthContext'

export interface EntityObject {
  id: string
  goid: string
  name: string
  type?: string
  display_name?: string
  source_id?: string
  tank_size?: number | null
  created_at?: string
  updated_at?: string
}

interface BasicEntityInfo {
  name: string
  goid: string
  tank_size?: number | null
}

/** Flat cache mapping GOID to entity info */
type EntityCache = Record<string, BasicEntityInfo>

interface EntityCacheContextType {
  getEntity: (objectType: string, goid: string) => EntityObject | null
  getCachedEntities: (objectType: string) => Record<string, EntityObject>
  addEntity: (objectType: string, entity: EntityObject) => void
  ensureEntitiesLoaded: (objectType: string) => Promise<void>
  isLoading: boolean
}

/** Cache refresh interval: 30 minutes */
const CACHE_REFRESH_MS = 30 * 60 * 1000

const EntityCacheContext = createContext<EntityCacheContextType | undefined>(
  undefined,
)

export function EntityCacheProvider({ children }: { children: ReactNode }) {
  const [cache, setCache] = useState<EntityCache>({})
  const [isLoading, setIsLoading] = useState(false)
  const { isAuthenticated } = useAuth()
  const { selectedTenant } = useTenantStore()

  /** Fetch all entities from the unified goid_map endpoint */
  const fetchAllEntities = useCallback(async () => {
    setIsLoading(true)

    try {
      const { data, error, response } = await fetchClient.POST(
        '/crossroads/entity/goid_map',
      )

      if (error || !response.ok) {
        console.warn('Failed to fetch entity goid_map:', response.status)
        return
      }

      const newCache: EntityCache = {}
      for (const entity of data ?? []) {
        if (entity.goid) {
          newCache[entity.goid] = entity
        }
      }

      setCache(newCache)
      console.log(`✅ Cached ${Object.keys(newCache).length} entities`)
    } catch (error) {
      console.error('Failed to fetch entity goid_map:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch on mount and refresh every 30 minutes, once the user's tenant is known
  useEffect(() => {
    if (!isAuthenticated || !selectedTenant) {
      return
    }

    // Initial fetch
    fetchAllEntities()

    // Set up refresh interval
    const intervalId = setInterval(fetchAllEntities, CACHE_REFRESH_MS)

    return () => clearInterval(intervalId)
  }, [isAuthenticated, selectedTenant, fetchAllEntities])

  /** Kept for API compatibility - cache is loaded on mount */
  const ensureEntitiesLoaded = useCallback(async (_objectType: string) => {
    // No-op: cache is loaded on mount and refreshed automatically
  }, [])

  /** Get a single entity from cache. objectType is ignored since GOIDs are unique. */
  const getEntity = useCallback(
    (_objectType: string, goid: string): BasicEntityInfo | null => {
      return cache[goid] || null
    },
    [cache],
  )

  /** Get all cached entities. objectType is ignored - returns all entities. */
  const getCachedEntities = useCallback(
    (_objectType: string): Record<string, BasicEntityInfo> => {
      return cache
    },
    [cache],
  )

  /** Add a single entity to the cache */
  const addEntity = useCallback((_objectType: string, entity: EntityObject) => {
    if (!entity.goid) {
      console.warn('Cannot add entity without GOID:', entity)
      return
    }

    setCache((prev) => ({
      ...prev,
      [entity.goid]: {
        name: entity.name,
        goid: entity.goid,
        tank_size: entity.tank_size,
      },
    }))
  }, [])

  return (
    <EntityCacheContext.Provider
      value={{
        getEntity,
        getCachedEntities,
        addEntity,
        ensureEntitiesLoaded,
        isLoading,
      }}
    >
      {children}
    </EntityCacheContext.Provider>
  )
}

export function useEntityCache() {
  const context = useContext(EntityCacheContext)
  if (context === undefined) {
    throw new Error('useEntityCache must be used within an EntityCacheProvider')
  }
  return context
}
